
import { MOCK_RESTAURANTS, MOCK_POS_ORDERS, MOCK_PROMOTIONS, MOCK_VALIDATION_CODES, MOCK_CAMPAIGNS, MOCK_MENU, MOCK_CUSTOMERS } from './constants';
import type { User, Restaurant, MenuItem, FlashPromotion, Campaign, Customer, POSOrder, Reservation } from './types';
import type { AnalyticsRaw } from './analytics/types';


const STORAGE_KEY = 'atable_data_store';
const USER_KEY = 'atable_current_user';

class MockBackend {
    private data: {
        users: User[];
        restaurants: Restaurant[];
        posOrders: POSOrder[];
        campaigns: Campaign[];
        flashPromotions: FlashPromotion[];
        reservations: Reservation[];
        flashReservations: Map<string, Set<string>>; // userId -> Set of flashPromoId
        memberships: any[];
        /** Visites validées (absentes par défaut ; alimentées par les tests e2e). */
        visits?: any[];
        /** Programme publié par restaurant (absent : programme de démonstration). */
        loyaltyPrograms?: Record<string, any>;
    };
    private currentUser: User | null = null;
    private listeners: Function[] = [];

    constructor() {
        // Récupérer l'utilisateur connecté
        const savedUser = localStorage.getItem(USER_KEY);
        if (savedUser) {
            this.currentUser = JSON.parse(savedUser);
        }

        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            this.data = JSON.parse(saved);
        } else {
            this.data = {
                users: [
                    { id: 'u1', email: 'admin@atable.test', role: 'ADMIN', firstName: 'Super', lastName: 'Admin', createdAt: new Date().toISOString(), status: 'ACTIVE' },
                    { id: 'u2', email: 'resto@atable.test', role: 'RESTAURATEUR', firstName: 'Jean', lastName: 'Bistrot', createdAt: new Date().toISOString(), status: 'ACTIVE', restaurantId: 1 },
                    { id: 'u3', email: 'client@atable.test', role: 'CLIENT', firstName: 'Client', lastName: 'Demo', createdAt: new Date().toISOString(), status: 'ACTIVE' },
                ],
                restaurants: [...MOCK_RESTAURANTS],
                posOrders: [...MOCK_POS_ORDERS],
                campaigns: [...MOCK_CAMPAIGNS] as any,
                flashPromotions: [
                    {
                        id: 'flash-init-1',
                        restaurantId: 1,
                        menuItemId: 'm1',
                        itemName: 'Burger Classic',
                        originalPrice: 14.50,
                        discountPrice: 9.90,
                        quantityTotal: 20,
                        quantityRemaining: 8,
                        date: new Date().toISOString().slice(0, 10),
                        startTime: '11:00',
                        endTime: '23:00',
                        active: true,
                        createdAt: new Date().toISOString()
                    }
                ],
                reservations: [],
                flashReservations: new Map(),
                memberships: []
            };
            this.save();
        }
    }

    private save() {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
        // Sauvegarder aussi l'utilisateur connecté
        if (this.currentUser) {
            localStorage.setItem(USER_KEY, JSON.stringify(this.currentUser));
        } else {
            localStorage.removeItem(USER_KEY);
        }
    }

    subscribe(listener: Function) {
        this.listeners.push(listener);
        listener();
        return () => { this.listeners = this.listeners.filter(l => l !== listener); };
    }

    private notifyListeners() {
        this.save();
        this.listeners.forEach(l => l());
    }

    // --- AUTH ---
    async login(email: string): Promise<User> {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const user = this.data.users.find(u => u.email.toLowerCase() === email.toLowerCase());
                if (user) {
                    this.currentUser = user;
                    this.notifyListeners();
                    resolve(user);
                }
                else reject(new Error("Utilisateur non trouvé."));
            }, 500);
        });
    }

    async registerClient(email: string, firstName: string, lastName: string) {
        return new Promise<User>((resolve) => {
            setTimeout(() => {
                const newUser: User = {
                    id: `u-${Date.now()}`,
                    email,
                    role: 'CLIENT',
                    firstName,
                    lastName,
                    createdAt: new Date().toISOString(),
                    status: 'ACTIVE'
                };
                this.data.users.push(newUser);
                this.currentUser = newUser;
                this.notifyListeners();
                resolve(newUser);
            }, 800);
        });
    }

    logout() {
        this.currentUser = null;
        localStorage.removeItem(USER_KEY);
        this.notifyListeners();
    }

    getCurrentUser() { return this.currentUser; }

    getRestaurants() { return this.data.restaurants.filter(r => r.status === 'ACTIVE'); }

    async getRestaurant(restaurantId: number): Promise<Restaurant | null> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const restaurant = this.data.restaurants.find(r => r.id === restaurantId);
                resolve(restaurant || null);
            }, 300);
        });
    }

    async updateRestaurantMenu(restaurantId: number, menu: MenuItem[]): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const idx = this.data.restaurants.findIndex(r => r.id === restaurantId);
                if (idx !== -1) {
                    this.data.restaurants[idx].menu = menu;
                    this.notifyListeners();
                }
                resolve();
            }, 800);
        });
    }

    async updateRestaurantProfile(restaurantId: number, data: any): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const idx = this.data.restaurants.findIndex(r => r.id === restaurantId);
                if (idx !== -1) {
                    this.data.restaurants[idx] = { ...this.data.restaurants[idx], ...data };
                    this.notifyListeners();
                }
                resolve();
            }, 800);
        });
    }

    async createFlashPromotion(promo: Omit<FlashPromotion, 'id' | 'createdAt'>): Promise<FlashPromotion> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const newPromo: FlashPromotion = {
                    ...promo,
                    id: `flash-${Date.now()}`,
                    createdAt: new Date().toISOString()
                };
                this.data.flashPromotions.push(newPromo);

                const newCamp: Campaign = {
                    id: `camp-flash-${Date.now()}`,
                    restaurantId: promo.restaurantId,
                    name: `⚡ Flash : ${promo.itemName}`,
                    type: 'flash',
                    status: 'active',
                    stats: { openRate: 0, conversionRate: 0, revenue: 0 },
                    description: `Vente flash sur ${promo.itemName} ! ${promo.discountPrice}€ au lieu de ${promo.originalPrice}€.`,
                    targetSegment: promo.targetSegment || 'all',
                    createdAt: new Date().toISOString(),
                    flashPromoId: newPromo.id
                };
                this.data.campaigns.unshift(newCamp);

                this.notifyListeners();
                resolve(newPromo);
            }, 1200);
        });
    }

    getCampaigns(restaurantId: number) {
        return this.data.campaigns.filter(c => c.restaurantId === restaurantId);
    }

    getFlashPromotions(restaurantId: number) {
        return this.data.flashPromotions.filter(p => p.restaurantId === restaurantId && p.active);
    }

    /** Toutes les ventes flash du restaurant (y compris passées / arrêtées), comme firestoreService. */
    async getRestaurantFlashPromotions(restaurantId: number | string): Promise<FlashPromotion[]> {
        const rid = Number(restaurantId);
        return this.data.flashPromotions.filter(p => p.restaurantId === rid);
    }

    async updateFlashPromotion(id: string, fields: Partial<FlashPromotion>): Promise<void> {
        this.data.flashPromotions = this.data.flashPromotions.map(p => p.id === id ? { ...p, ...fields, active: true } : p);
        this.save(); this.notifyListeners();
    }

    async stopFlashPromotion(id: string): Promise<void> {
        this.data.flashPromotions = this.data.flashPromotions.map(p => p.id === id ? { ...p, active: false } : p);
        this.save(); this.notifyListeners();
    }

    async deleteFlashPromotion(id: string): Promise<void> {
        this.data.flashPromotions = this.data.flashPromotions.filter(p => p.id !== id);
        this.save(); this.notifyListeners();
    }

    getAllActiveFlashPromotions() {
        return this.data.flashPromotions.filter(p => p.active);
    }

    async getPOSOrders(restaurantId?: number): Promise<POSOrder[]> {
        return new Promise((resolve) => {
            setTimeout(() => {
                if (restaurantId) {
                    resolve(this.data.posOrders.filter(o => o.restaurantId === restaurantId));
                } else {
                    resolve(this.data.posOrders);
                }
            }, 300);
        });
    }

    savePOSOrder(order: POSOrder) {
        const idx = this.data.posOrders.findIndex(o => o.id === order.id);
        if (idx !== -1) {
            this.data.posOrders[idx] = order;
        } else {
            this.data.posOrders.push(order);
        }
        this.notifyListeners();
    }

    deletePOSOrder(id: string) { this.data.posOrders = this.data.posOrders.filter(o => o.id !== id); this.notifyListeners(); }

    /** Identifiant d'une nouvelle commande de caisse (aléatoire, comme Firestore). */
    newPOSOrderId(): string {
        return `cmd-${Math.random().toString(36).slice(2, 12)}`;
    }

    /** Membres du restaurant pour l'identification en caisse (sans avatar tiers). */
    async getRestaurantMembersForPOS(restaurantId: number | string) {
        return this.data.memberships
            .filter(m => String(m.restaurantId) === String(restaurantId))
            .map(m => {
                const u = this.data.users.find(x => x.id === m.userId);
                return {
                    id: m.userId,
                    name: u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Client' : 'Client',
                    status: m.grade || 'Nouveau',
                    points: m.points || 0,
                    stamps: m.stamps || 0,
                };
            });
    }

    /**
     * Encaissement (mode démo) : même contrat que la Cloud Function payPosOrder — commande payée,
     * fidélité créditée au membre lié sauf refus (déjà validé aujourd'hui, ≥ 1 000 €…).
     * `window.__ATABLE_MOCK_FAULTS__.payOrder = 'raison'` simule une panne (tests e2e uniquement).
     */
    async payOrder(id: string, method: string) {
        await new Promise((r) => setTimeout(r, 300));
        const fault = (globalThis as any).__ATABLE_MOCK_FAULTS__?.payOrder;
        if (fault) {
            throw Object.assign(new Error(fault === 'no_response'
                ? 'Réponse non reçue : la commande est peut-être déjà payée. Vérifiez l\'historique avant de réencaisser.'
                : 'Service d\'encaissement indisponible. Réessayez dans un instant.'), { reason: fault });
        }
        const order = this.data.posOrders.find(o => o.id === id);
        if (!order) throw Object.assign(new Error('Commande introuvable.'), { reason: 'not-found' });
        if (order.status === 'paid') return { success: true as const, paid: true as const, credited: false, reason: 'already_paid' };

        order.status = 'paid';
        order.paymentMethod = method;
        (order as any).paidAt = new Date().toISOString();

        const amountCents = Math.round((order.total || 0) * 100);
        const customerId = order.customer?.id ? String(order.customer.id) : null;
        const rid = String(order.restaurantId);
        const program = await this.getLoyaltyProgram(rid);
        const today = new Date().toDateString();
        const membership = customerId
            ? this.data.memberships.find(m => m.userId === customerId && String(m.restaurantId) === rid)
            : null;
        let result: any = { success: true, paid: true, credited: false, programType: program.type };
        if (!customerId) result.reason = 'no_customer';
        else if (!membership) result.reason = 'not_member';
        else if (amountCents <= 0) result.reason = 'no_amount';
        else if (amountCents >= 100_000) result.reason = 'amount_too_high';
        else if ((this.data.visits ?? []).some(v => v.userId === customerId && String(v.restaurantId) === rid
            && new Date(v.createdAt).toDateString() === today)) result.reason = 'already_validated_today';
        else {
            const stamps = program.type === 'stamps' ? 1 : 0;
            const points = program.type === 'stamps' ? 0 : Math.floor((amountCents / 100) * (program.generosity || 1));
            membership.points = (membership.points || 0) + points;
            membership.stamps = (membership.stamps || 0) + stamps;
            membership.lastVisitAt = new Date().toISOString();
            this.data.visits = [...(this.data.visits ?? []), {
                userId: customerId, restaurantId: order.restaurantId, amount: amountCents, pointsEarned: points,
                stampsEarned: stamps, createdAt: new Date().toISOString(), validationMethod: 'pos',
            }];
            result = { ...result, credited: true, amountCents, pointsEarned: points, stampsEarned: stamps, clientName: order.customer?.name };
        }
        this.notifyListeners();
        return result;
    }

    /** Programme de fidélité (mode démo) : le dernier publié, sinon celui de démonstration. */
    async getLoyaltyProgram(restaurantId: number | string) {
        return this.data.loyaltyPrograms?.[String(restaurantId)] ?? {
            id: String(restaurantId),
            restaurantId: String(restaurantId),
            type: 'points',
            generosity: 1,
            targetCount: 50,
            welcomeBonus: 10,
            rewardLabel: 'Café offert',
            rewardTiers: [],
        };
    }

    async saveLoyaltyProgram(restaurantId: number | string, config: any): Promise<{ previousType: string | null }> {
        const previous = await this.getLoyaltyProgram(restaurantId);
        this.data.loyaltyPrograms = {
            ...(this.data.loyaltyPrograms ?? {}),
            [String(restaurantId)]: { ...config, id: String(restaurantId), restaurantId: String(restaurantId), updatedAt: new Date().toISOString() },
        };
        this.notifyListeners();
        return { previousType: previous?.type ?? null };
    }

    /** Conversion des adhésions (mode démo) : compte les membres, comme la Cloud Function. */
    async migrateMemberships(restaurantId: number | string, _oldType: string): Promise<{ migratedCount: number }> {
        const program = await this.getLoyaltyProgram(restaurantId);
        let migratedCount = 0;
        for (const m of this.data.memberships) {
            if (String(m.restaurantId) !== String(restaurantId) || m.programType === program.type) continue;
            m.programType = program.type;
            migratedCount += 1;
        }
        this.notifyListeners();
        return { migratedCount };
    }

    /** Règles de segmentation par défaut (mode mock : rien n'est persisté). */
    async getSegmentRules(_restaurantId: number | string) {
        const { mergeRules } = await import('./customerSegmentation');
        return { rules: mergeRules(null), lastComputedAt: null };
    }

    /**
     * Analyses (mode mock) : uniquement ce que le mock contient réellement
     * (commandes caisse). Aucune donnée inventée : sans visite validée,
     * l'écran affiche son état vide, comme en production.
     */
    async getAnalyticsRaw(restaurantId: number | string): Promise<AnalyticsRaw> {
        const rid = Number(restaurantId);
        const asDate = (v: any) => (v ? new Date(v) : null);
        return {
            visits: (this.data.visits ?? []).filter(v => Number(v.restaurantId) === rid).map(v => ({
                userId: v.userId ?? null, amount: v.amount ?? null, pointsEarned: v.pointsEarned ?? null,
                stampsEarned: v.stampsEarned ?? null, createdAt: asDate(v.createdAt), validationMethod: v.validationMethod ?? null,
            })),
            orders: this.data.posOrders.filter(o => o.restaurantId === rid).map(o => ({
                customerId: o.customer?.id != null ? String(o.customer.id) : null,
                total: o.total, status: o.status,
                paidAt: o.createdAt ? new Date(o.createdAt) : null,
                createdAt: o.createdAt ? new Date(o.createdAt) : null,
            })),
            memberships: (this.data.memberships ?? []).filter(m => Number(m.restaurantId) === rid).map(m => ({
                userId: String(m.userId), points: m.points ?? null, stamps: m.stamps ?? null, tier: m.tier ?? null, grade: m.grade ?? null,
                joinedDate: asDate(m.joinedDate), lastVisitAt: asDate(m.lastVisitAt),
            })),
            redemptions: [],
            segments: [],
        };
    }

    async getAnalytics(restaurantId: number) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const orders = this.data.posOrders.filter(o => o.status === 'paid' && o.restaurantId === restaurantId);
                const totalRevenue = orders.reduce((acc, o) => acc + o.total, 0);
                const totalVisits = orders.length;
                const averageTicket = totalVisits > 0 ? totalRevenue / totalVisits : 0;

                resolve({
                    totalRevenue: Math.round(totalRevenue * 100) / 100,
                    totalVisits,
                    averageTicket: Math.round(averageTicket * 100) / 100,
                    loyaltyRate: 65, // Mock
                    newCustomers: 45, // Mock
                    returningCustomers: 120, // Mock
                    topItems: [], // Mock
                    revenueByDay: [], // Mock
                });
            }, 300);
        });
    }

    async getRestaurantCustomers(restaurantId: number | string): Promise<Customer[]> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const rIdNum = typeof restaurantId === 'string' ? parseInt(restaurantId) || 1 : restaurantId;
                const customers: Customer[] = MOCK_CUSTOMERS.map(c => ({
                    ...c,
                    id: c.id.toString(),
                })) as any;
                resolve(customers); // In a mock we just return the global mocked list
            }, 300);
        });
    }

    async createCampaign(campaign: Omit<Campaign, 'id' | 'stats' | 'createdAt'>) {
        return new Promise<void>((resolve) => {
            setTimeout(() => {
                const newCamp: Campaign = {
                    ...campaign,
                    id: `camp-${Date.now()}`,
                    stats: { openRate: 0, conversionRate: 0, revenue: 0 },
                    createdAt: new Date().toISOString()
                };
                this.data.campaigns.unshift(newCamp);
                this.notifyListeners();
                resolve();
            }, 800);
        });
    }

    async createReservation(res: Omit<Reservation, 'id' | 'createdAt'> & { flashPromoId?: string }) {
        return new Promise<Reservation>((resolve, reject) => {
            setTimeout(() => {
                // Check if this is a flash promotion reservation
                const flashPromoId = (res as any).flashPromoId;

                if (flashPromoId) {
                    // Validate flash reservation limit (1 per user per promo)
                    const userFlashReservations = this.data.flashReservations.get(res.userId) || new Set();

                    if (userFlashReservations.has(flashPromoId)) {
                        reject(new Error('Vous avez déjà réservé cette offre flash'));
                        return;
                    }

                    // Check if flash promo still has quantity
                    const flashPromo = this.data.flashPromotions.find(fp => fp.id === flashPromoId);
                    if (!flashPromo || flashPromo.quantityRemaining <= 0) {
                        reject(new Error('Cette offre flash n\'est plus disponible'));
                        return;
                    }

                    // Decrease quantity
                    flashPromo.quantityRemaining--;

                    // Track the reservation
                    userFlashReservations.add(flashPromoId);
                    this.data.flashReservations.set(res.userId, userFlashReservations);
                }

                // Create the reservation
                const newReservation: Reservation = {
                    ...res,
                    id: `res-${Date.now()}`,
                    createdAt: new Date().toISOString()
                };

                this.data.reservations.push(newReservation);
                this.notifyListeners();
                resolve(newReservation);
            }, 1000);
        });
    }

    getUsersAdmin() { return this.data.users; }
    getAllRestaurantsAdmin() { return this.data.restaurants; }

    deleteRestaurant(id: number) {
        this.data.restaurants = this.data.restaurants.filter(r => r.id !== id);
        this.notifyListeners();
    }

    toggleRestaurantStatus(id: number) {
        const idx = this.data.restaurants.findIndex(r => r.id === id);
        if (idx !== -1) {
            this.data.restaurants[idx].status = this.data.restaurants[idx].status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
            this.notifyListeners();
        }
    }

    toggleUserStatus(id: string) {
        const idx = this.data.users.findIndex(u => u.id === id);
        if (idx !== -1) {
            this.data.users[idx].status = this.data.users[idx].status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
            this.notifyListeners();
        }
    }

    async createRestaurateur(data: any) {
        return new Promise<void>((resolve) => {
            setTimeout(() => {
                const restaurantId = this.data.restaurants.length + 1;
                const newResto: Restaurant = {
                    id: restaurantId,
                    name: data.restaurantName,
                    cuisine: data.category,
                    offer: data.offer,
                    lat: 48.8566 + (Math.random() * 0.02 - 0.01),
                    lng: 2.3522 + (Math.random() * 0.02 - 0.01),
                    budget: data.budget,
                    popularity: 'Nouveau',
                    ambiance: data.ambiance,
                    distance: Math.floor(Math.random() * 1000),
                    status: 'ACTIVE',
                    aggregateRating: 0,
                    visitCount: 0,
                    rewardScore: 0,
                    menu: [...MOCK_MENU]
                };

                const newUser: User = {
                    id: `u-${Date.now()}`,
                    email: data.email,
                    role: 'RESTAURATEUR',
                    firstName: data.firstName,
                    lastName: data.lastName,
                    createdAt: new Date().toISOString(),
                    status: 'ACTIVE',
                    restaurantId: restaurantId
                };

                this.data.restaurants.push(newResto);
                this.data.users.push(newUser);
                this.notifyListeners();
                resolve();
            }, 1000);
        });
    }

    async validateVisit(userId: string, restaurantId: number, code: string, amount?: number) {
        return new Promise<{ success: boolean, pointsEarned: number, message?: string }>((resolve) => {
            setTimeout(() => {
                if (code === '1234' || code === 'BONUS') {
                    const points = amount ? Math.floor(amount * 1.5) : 50;
                    resolve({ success: true, pointsEarned: points });
                } else {
                    resolve({ success: false, pointsEarned: 0, message: "Code incorrect." });
                }
            }, 1000);
        });
    }

    activatePromo(userId: string, promoId: string) {
        console.log(`Promo ${promoId} activated for user ${userId}`);
    }

    getReservations(restaurantId?: number): Reservation[] {
        if (restaurantId) {
            return this.data.reservations.filter(r => r.restaurantId === restaurantId);
        }
        return this.data.reservations;
    }

    updateReservationStatus(reservationId: string, status: 'confirmed' | 'cancelled' | 'completed') {
        const reservation = this.data.reservations.find(r => r.id === reservationId);
        if (reservation) {
            // Comme en production : un refus rend le stock et le client peut réserver à nouveau.
            if (status === 'cancelled' && reservation.status !== 'cancelled' && reservation.flashPromoId) {
                const promo = this.data.flashPromotions.find(fp => fp.id === reservation.flashPromoId);
                if (promo) promo.quantityRemaining += reservation.offerQuantity ?? 1;
                this.data.flashReservations.get(reservation.userId)?.delete(reservation.flashPromoId);
            }
            reservation.status = status;
            this.notifyListeners();
        }
    }

    getUsers(): User[] {
        return this.data.users;
    }

    async getMemberships(userId: string) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const userMemberships = (this.data as any).memberships.filter((m: any) => m.userId === userId);
                resolve(userMemberships);
            }, 300);
        });
    }

    async joinLoyaltyProgram(userId: string, restaurantId: number | string): Promise<boolean> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const exists = (this.data as any).memberships.find((m: any) => m.userId === userId && String(m.restaurantId) === String(restaurantId));
                if (exists) {
                    resolve(true);
                    return;
                }

                const restaurant = this.data.restaurants.find(r => String(r.id) === String(restaurantId));
                const config = restaurant?.loyaltyConfig || { type: 'points', welcomeBonus: 0 };
                const welcomeBonus = config.welcomeBonus || 0;

                const newMembership = {
                    id: `mem-${Date.now()}`,
                    userId,
                    restaurantId: String(restaurantId),
                    points: config.type === 'points' || config.type === 'spending' ? welcomeBonus : 0,
                    stamps: config.type === 'stamps' ? welcomeBonus : 0,
                    tier: 'Bronze',
                    joinedDate: new Date().toISOString(),
                    createdAt: new Date().toISOString(),
                };

                (this.data as any).memberships.push(newMembership);
                this.notifyListeners();
                resolve(true);
            }, 500);
        });
    }

    async leaveLoyaltyProgram(userId: string, restaurantId: number | string): Promise<boolean> {
        return new Promise((resolve) => {
            setTimeout(() => {
                (this.data as any).memberships = (this.data as any).memberships.filter((m: any) => !(m.userId === userId && String(m.restaurantId) === String(restaurantId)));
                this.notifyListeners();
                resolve(true);
            }, 500);
        });
    }
}
export const mockBackend = new MockBackend();
